import React, { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Pills from '@/components/Pills';
import Tabs from '@/components/Tabs';
import NavLink from '@/components/NavLink';
import ExternalLink from '@/components/ExternalLink';
import Link from '@/components/Link';
import Button from '@/components/Button';
import Icon from '@/components/Icon';
import Collapse from '@/components/Collapse';
import {
  settingsFunnelsPath,
  settingsCustomFieldsPath,
  settingsProductsPath,
  settingsProductCategoriesPath,
  settingsEmailTemplatesPath,
  settingsModulesVoipPath,
  settingsVoipPanelPath,
  legacySettingsUrl
} from '@/routes';
import { useVoipVerify } from '@/api';
import { useAuth } from '@/lib/auth';

function SettingsBase() {
  const { pathname } = useLocation();
  const mainPath = '/settings/settings';
  const activePath = (path) => pathname.includes(`${mainPath}${path}`);

  const customFieldsPath = activePath('/custom_fields');
  const productsPath = activePath('/products');
  const emailPath = activePath('/email_template');
  const voipPath = activePath('/modules/voip') || activePath('/voip/panel');

  const subMenuClassName = 'height-2 fw-normal';
  const subMenuExpandedItemClassName = `${subMenuClassName} my-2 text-dark`;

  const [customFieldsMenuExpanded, setCustomFieldsExpanded] = useState(customFieldsPath);
  const [productsMenuExpanded, setProductsExpanded] = useState(productsPath);
  const [emailMenuExpanded, setEmailExpanded] = useState(emailPath);
  const [voipMenuExpanded, setVoipExpanded] = useState(voipPath);

  const closeExpandedItems = () => {
    setCustomFieldsExpanded(false);
    setProductsExpanded(false);
    setEmailExpanded(false);
    setVoipExpanded(false);
  };

  const toggleCustomFieldsMenu = () => {
    setCustomFieldsExpanded((prev) => !prev);
    setProductsExpanded(false);
    setEmailExpanded(false);
    setVoipExpanded(false);
  };

  const toggleProductsMenu = () => {
    setProductsExpanded((prev) => !prev);
    setCustomFieldsExpanded(false);
    setEmailExpanded(false);
    setVoipExpanded(false);
  };

  const toggleEmailMenu = () => {
    setEmailExpanded((prev) => !prev);
    setCustomFieldsExpanded(false);
    setProductsExpanded(false);
    setVoipExpanded(false);
  };

  const toggleVoipMenu = () => {
    setVoipExpanded((prev) => !prev);
    setCustomFieldsExpanded(false);
    setProductsExpanded(false);
    setEmailExpanded(false);
  };

  const { user } = useAuth();
  const { data: voip, isLoading: isLoadingVoip } = useVoipVerify({ config: { staleTime: 1000 } });

  if (isLoadingVoip) {
    return null;
  }

  const isAdmin = user?.admin;
  const hasVoipZenvia = voip?.balance > 0 || voip?.madeCallLast30Days;
  const showVoipTab = isAdmin && hasVoipZenvia;

  return (
    <div className='d-flex overflow-auto w-100'>
      <Pills
        className='nav-settings-pills width-5 overflow-y-auto flex-shrink-0'
      >
        <Tabs
          className='d-flex flex-column w-100 gap-5 p-4'
        >
          <Pills.Item>
            <NavLink
              onClick={closeExpandedItems}
              href={settingsFunnelsPath()}
              className={subMenuClassName}
              end
            >
              <Icon name='funnel' size='sm' className='me-2' />
              Funis e etapas
            </NavLink>
          </Pills.Item>

          <Pills.Item>
            <NavLink
              as={ExternalLink}
              href={legacySettingsUrl('MotivoPerda')}
              className={subMenuClassName}
              end
            >
              <Icon name='lost-deal' size='sm' className='me-2' />
              Motivos de Perda
            </NavLink>
          </Pills.Item>

          <Pills.Item>
            <NavLink
              as={ExternalLink}
              href={legacySettingsUrl('OrigemLead')}
              className={subMenuClassName}
              end
            >
              <Icon name='filter' size='sm' className='me-2' />
              Origens de clientes
            </NavLink>
          </Pills.Item>

          <Pills.Item>
            <NavLink
              as={ExternalLink}
              href={legacySettingsUrl('Categorias')}
              className={subMenuClassName}
              end
            >
              <Icon name='team' size='sm' className='me-2' />
              Categorias de clientes
            </NavLink>
          </Pills.Item>

          <Pills.Item>
            <NavLink
              as={ExternalLink}
              href={legacySettingsUrl('Setores')}
              className={subMenuClassName}
              end
            >
              <Icon name='organization' size='sm' className='me-2' />
              Setores
            </NavLink>
          </Pills.Item>

          <Pills.Item className={
            { 'bg-light text-nowrap rounded p-2': customFieldsMenuExpanded }
          }>
            <NavLink
              as={Button}
              onClick={toggleCustomFieldsMenu}
              href={settingsCustomFieldsPath()}
              variant='link'
              className={subMenuClassName}
              active={false}
              end
            >
              <Icon name='edit' size='sm' className='me-2' />
                Campos customizados
              <Icon
                name={customFieldsMenuExpanded ? 'arrow-up' : 'arrow-down'}
                className='ms-auto'
              />
            </NavLink>

            <Collapse in={customFieldsMenuExpanded}>
              <div>
                <NavLink
                  as={Link}
                  href={settingsCustomFieldsPath('organizations')}
                  className={subMenuExpandedItemClassName}
                  active={activePath('/custom_fields/organizations')}
                  end
                >
                  <Icon name='organization' size='sm' className='me-2' />
                  Formulário de empresa
                </NavLink>

                <NavLink
                  as={Link}
                  href={settingsCustomFieldsPath('people')}
                  className={subMenuExpandedItemClassName}
                  active={activePath('/custom_fields/people')}
                  end
                >
                  <Icon name='person' size='sm' className='me-2' />
                  Formulário de pessoa
                </NavLink>

                <NavLink href={settingsCustomFieldsPath('deals')}
                  as={Link}
                  className={subMenuExpandedItemClassName}
                  active={activePath('/custom_fields/deals')}
                  end
                >
                  <Icon name='deal' size='sm' className='me-2' />
                  Formulário de negócio
                </NavLink>
              </div>
            </Collapse>
          </Pills.Item>

          <Pills.Item className={ { 'bg-light text-nowrap rounded p-2': productsMenuExpanded } }>
            <NavLink
              as={Button}
              onClick={toggleProductsMenu}
              href={settingsProductsPath()}
              variant='link'
              className={subMenuClassName}
              active={false}
              end
            >
              <Icon name='product' size='sm' className='me-2' />
              Produtos e serviços
              <Icon
                name={productsMenuExpanded ? 'arrow-up' : 'arrow-down'}
                className='ms-auto'
              />
            </NavLink>

            <Collapse in={productsMenuExpanded}>
              <div>
                <NavLink
                  as={Link}
                  href={settingsProductsPath()}
                  className={subMenuExpandedItemClassName}
                  active={activePath('/products') && !activePath('/products/')}
                  end
                >
                  <Icon name='listing' size='sm' className='me-1' />
                  Listagem de produtos
                </NavLink>

                <NavLink
                  as={Link}
                  href={settingsProductCategoriesPath()}
                  className={subMenuExpandedItemClassName}
                  active={activePath('/products/categories')}
                  end
                >
                  <Icon name='tag' size='sm' className='me-1' />
                  Categorias
                </NavLink>
              </div>
            </Collapse>
          </Pills.Item>

          <Pills.Item className={ { 'bg-light text-nowrap rounded p-2': emailMenuExpanded } }>
            <NavLink
              as={Button}
              onClick={toggleEmailMenu}
              href={settingsEmailTemplatesPath()}
              variant='link'
              className={subMenuClassName}
              active={false}
              end
            >
              <Icon name='email' size='sm' className='me-2' />
                Email
              <Icon
                name={emailMenuExpanded ? 'arrow-up' : 'arrow-down'}
                className='ms-auto'
              />
            </NavLink>

            <Collapse in={emailMenuExpanded}>
              <div>
                <NavLink
                  as={Link}
                  href={settingsEmailTemplatesPath()}
                  className={subMenuExpandedItemClassName}
                  active={activePath('/email_template')}
                  end
                >
                  <Icon name='listing' size='sm' className='me-2' />
                  Modelos de e-mail
                </NavLink>

                <NavLink
                  as={ExternalLink}
                  href={legacySettingsUrl('Dropbox')}
                  className={subMenuExpandedItemClassName}
                >
                  <Icon name='email' size='sm' className='me-2' />
                  E-mail da conta
                </NavLink>
              </div>
            </Collapse>
          </Pills.Item>

          {showVoipTab
            ? (
              <Pills.Item className={ { 'bg-light text-nowrap rounded p-2': voipMenuExpanded } }>
                <NavLink
                  as={Button}
                  onClick={toggleVoipMenu}
                  href={settingsModulesVoipPath()}
                  variant='link'
                  className={subMenuClassName}
                  active={false}
                  end
                >
                  <Icon name='call' size='sm' className='me-2' />
                    Telefone Virtual
                  <Icon
                    name={voipMenuExpanded ? 'arrow-up' : 'arrow-down'}
                    className='ms-auto'
                  />
                </NavLink>

                <Collapse in={voipMenuExpanded}>
                  <div>
                    <NavLink
                      as={Link}
                      href={settingsModulesVoipPath()}
                      className={subMenuExpandedItemClassName}
                      active={activePath('/modules/voip')}
                      end
                    >
                      <Icon name='power' size='sm' className='me-2' />
                      Ativar telefone
                    </NavLink>

                    <NavLink
                      as={Link}
                      href={settingsVoipPanelPath()}
                      className={subMenuExpandedItemClassName}
                      active={activePath('/voip/panel')}
                      end
                    >
                      <Icon name='report' size='sm' className='me-2' />
                      Painel
                    </NavLink>
                  </div>
                </Collapse>
              </Pills.Item>
            )
            : null
          }

          <Pills.Item>
            <NavLink
              as={ExternalLink}
              href={legacySettingsUrl('Email')}
              className={subMenuClassName}
              end
            >
              <Icon name='notification' size='sm' className='me-2' />
              Notificações
            </NavLink>
          </Pills.Item>
        </Tabs>
      </Pills>

      <Outlet />
    </div>
  );
}

export default SettingsBase;
